import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { C } from '../../albumColors';
import { VISIBLE_TOTAL } from '../../albumData';

function UserSearchCard({ allUsers, onViewUser, theme, myCommunities, onExpand }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const communityNameById = React.useMemo(() => {
    const m = new Map();
    for (const c of myCommunities || []) m.set(c.id, c.name);
    return m;
  }, [myCommunities]);
  const communitiesLabel = (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return null;
    const names = ids.map(id => communityNameById.get(id)).filter(Boolean);
    if (names.length === 0) return null;
    if (names.length <= 2) return names.join(' · ');
    return `${names[0]} · +${names.length - 1}`;
  };

  const sortedUsers = React.useMemo(
    () => [...(allUsers || [])].sort((a, b) => (b.owned ?? 0) - (a.owned ?? 0)),
    [allUsers]
  );

  const results = query.trim()
    ? sortedUsers.filter(u => u.name.toLowerCase().includes(query.toLowerCase()))
    : sortedUsers.slice(0, 4);

  const inputBg     = theme?.searchInputBg    ?? C.surfaceContainerHighest;
  const inputText   = theme?.searchInputText  ?? C.onSurface;
  const iconColor   = theme?.searchIconColor  ?? C.outline;
  const hoverBg     = theme?.searchHoverBg    ?? theme?.cardRowHoverBg ?? C.surfaceContainerHigh;
  const headingText = theme?.cardHeadingText ?? C.onSurfaceVariant;
  const bodyText    = theme?.cardBodyText    ?? C.onSurface;
  const subtleText  = theme?.cardSubtleText  ?? C.outline;
  const chevron     = theme?.cardSubtleText  ?? C.outlineVariant;
  const accent      = theme?.primaryAccent   ?? C.primary;

  const totalCollectors = (allUsers || []).length;
  const visibleResults = results.slice(0, 5);
  const hiddenCount = Math.max(0, totalCollectors - visibleResults.length);

  return (
    <section className="mb-8">
      <h4 className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: headingText }}>
        {t('Buscar coleccionistas')}
      </h4>
      <div className="relative mb-3">
        <div className="absolute inset-y-0 left-2.5 flex items-center pointer-events-none" style={{ color: iconColor }}>
          <span className="material-symbols-outlined text-sm">search</span>
        </div>
        <input
          className="w-full border-none rounded-lg pl-8 pr-3 py-2 text-xs focus:outline-none focus:ring-2"
          style={{ backgroundColor: inputBg, color: inputText, focusRingColor: `${accent}4d` }}
          placeholder={t('Buscar por nombre...')}
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        {visibleResults.map(user => {
          const pct = Math.round((user.owned / VISIBLE_TOTAL) * 100);
          return (
            <a
              key={user.id}
              onClick={() => { onViewUser(user); setQuery(''); }}
              className="flex items-center gap-3 p-2 rounded-lg transition-all cursor-pointer"
              onMouseEnter={e => e.currentTarget.style.backgroundColor = hoverBg}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0"
                style={{ backgroundColor: user.color }}
              >
                {user.initial}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold truncate" style={{ color: bodyText }}>{user.name}</p>
                {communitiesLabel(user.communityIds) && (
                  <p className="text-[10px] truncate" style={{ color: subtleText }}>
                    {communitiesLabel(user.communityIds)}
                  </p>
                )}
                <p className="text-[9px]" style={{ color: subtleText }}>{pct}% {t('completo')}</p>
              </div>
              <span className="material-symbols-outlined text-sm" style={{ color: chevron }}>chevron_right</span>
            </a>
          );
        })}
        {results.length === 0 && (
          <p className="text-xs" style={{ color: subtleText }}>{t('Sin coleccionistas')}</p>
        )}
      </div>
      {onExpand && hiddenCount > 0 && (
        <button
          type="button"
          onClick={onExpand}
          className="mt-3 w-full inline-flex items-center justify-center gap-1 text-[11px] font-bold rounded-lg px-3 py-2 hover:bg-black/5 transition-colors border border-transparent hover:border-black/5"
          style={{ color: accent }}
        >
          {t('Ver {{count}} más', { count: hiddenCount })}
          <span className="material-symbols-outlined text-sm">unfold_more</span>
        </button>
      )}
    </section>
  );
}

export default UserSearchCard;
